/**
 * Navigation Analytics Hook
 * Phase 3 Day 4: Track user navigation patterns for intelligent prefetching
 *
 * Tracks route transitions to identify common navigation flows and enable
 * smart prefetching of likely next pages.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Navigation pattern storage (in-memory for performance)
const navigationPatterns = new Map();
const transitionHistory = [];
const MAX_HISTORY_SIZE = 100;

/**
 * Record a navigation transition
 * @param {string} from - Previous route
 * @param {string} to - Current route
 */
function recordTransition(from, to) {
  if (!from || from === to) return;

  // Create transition key
  const key = `${from} → ${to}`;

  // Update transition count
  const current = navigationPatterns.get(key) || { count: 0, from, to };
  current.count++;
  current.lastSeen = Date.now();
  navigationPatterns.set(key, current);

  // Add to history (ring buffer)
  transitionHistory.push({ from, to, timestamp: Date.now() });
  if (transitionHistory.length > MAX_HISTORY_SIZE) {
    transitionHistory.shift();
  }

  // Store in localStorage for persistence (debounced)
  if (typeof window !== 'undefined') {
    const patterns = Array.from(navigationPatterns.entries());
    try {
      localStorage.setItem('nav_patterns', JSON.stringify(patterns));
    } catch (e) {
      // Ignore quota errors
    }
  }
}

/**
 * Get most likely next routes from current route
 * @param {string} currentRoute - Current route path
 * @param {number} limit - Number of suggestions to return
 * @returns {Array} Array of likely next routes with probabilities
 */
export function getLikelyNextRoutes(currentRoute, limit = 3) {
  const suggestions = [];

  // Find all transitions from current route
  for (const [key, data] of navigationPatterns.entries()) {
    if (data.from === currentRoute) {
      suggestions.push({
        route: data.to,
        count: data.count,
        lastSeen: data.lastSeen
      });
    }
  }

  // Sort by count (descending) and recency
  suggestions.sort((a, b) => {
    const countDiff = b.count - a.count;
    if (countDiff !== 0) return countDiff;
    return b.lastSeen - a.lastSeen;
  });

  // Calculate probabilities
  const total = suggestions.reduce((sum, s) => sum + s.count, 0);
  const results = suggestions.slice(0, limit).map(s => ({
    route: s.route,
    count: s.count,
    probability: total > 0 ? (s.count / total) : 0,
    confidence: s.count >= 3 ? 'high' : s.count >= 2 ? 'medium' : 'low'
  }));

  return results;
}

/**
 * Get navigation statistics
 * @returns {Object} Navigation analytics summary
 */
export function getNavigationStats() {
  const totalTransitions = transitionHistory.length;
  const uniquePatterns = navigationPatterns.size;
  const recentTransitions = transitionHistory.slice(-10);

  // Find most common transitions
  const topPatterns = Array.from(navigationPatterns.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
    .map(p => ({
      pattern: `${p.from} → ${p.to}`,
      count: p.count
    }));

  return {
    totalTransitions,
    uniquePatterns,
    recentTransitions,
    topPatterns
  };
}

/**
 * Load navigation patterns from localStorage
 */
function loadPatterns() {
  if (typeof window === 'undefined') return;

  try {
    const stored = localStorage.getItem('nav_patterns');
    if (stored) {
      const patterns = JSON.parse(stored);
      patterns.forEach(([key, value]) => {
        navigationPatterns.set(key, value);
      });
    }
  } catch (e) {
    console.warn('[Navigation Analytics] Failed to load patterns:', e);
  }
}

/**
 * Navigation Analytics Hook
 * Automatically tracks navigation between routes
 *
 * @returns {Object} Analytics utilities
 */
export function useNavigationAnalytics() {
  const location = useLocation();
  const previousLocation = useRef(null);
  const isInitialized = useRef(false);

  // Load patterns on mount
  useEffect(() => {
    if (!isInitialized.current) {
      loadPatterns();
      isInitialized.current = true;
    }
  }, []);

  // Track route changes
  useEffect(() => {
    const currentPath = location.pathname;

    // Record transition
    if (previousLocation.current && previousLocation.current !== currentPath) {
      recordTransition(previousLocation.current, currentPath);

      // Log for debugging (Phase 3 only)
      if (process.env.NODE_ENV === 'development') {
        const suggestions = getLikelyNextRoutes(currentPath, 3);
        if (suggestions.length > 0) {
          console.log('[Navigation Analytics]', currentPath);
          console.log('  Likely next routes:', suggestions);
        }
      }
    }

    previousLocation.current = currentPath;
  }, [location.pathname]);

  return {
    getLikelyNextRoutes: (route) => getLikelyNextRoutes(route || location.pathname),
    getNavigationStats,
    currentRoute: location.pathname
  };
}

/**
 * Prefetch likely next routes (for use in components)
 * @param {string} currentRoute - Current route
 * @param {number} threshold - Minimum probability to prefetch (0-1)
 */
export function prefetchLikelyRoutes(currentRoute, threshold = 0.3) {
  const suggestions = getLikelyNextRoutes(currentRoute, 5);

  suggestions
    .filter(s => s.probability >= threshold && s.confidence !== 'low')
    .forEach(suggestion => {
      // Prefetch using native link prefetch
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = suggestion.route;
      link.as = 'document';
      document.head.appendChild(link);

      if (process.env.NODE_ENV === 'development') {
        console.log(`[Prefetch] ${suggestion.route} (${(suggestion.probability * 100).toFixed(0)}% likely)`);
      }
    });
}

export default useNavigationAnalytics;
